import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Logo from '../components/Logo';
import MarkdownRenderer from '../components/MicroLearning/MarkdownRenderer';
import MediaDisplay, { MediaItem } from '../components/MicroLearning/MediaDisplay';
import CopyLinkButton from '../components/CopyLinkButton';
import ShareButton from '../components/ShareButton';
import {
  captureShareCardNode,
  downloadBundle,
  nativeShareBundle,
  type ShareCardBundle,
  type ShareCardInput,
} from '../components/ShareCard';
import ShareCardRender from '../components/ShareCardRender';
import './PublicQuestion.css';

interface QuestionView {
  question_id: string;
  /** Opaque 8-char base62 slug — prefer for share URLs to prevent
      enumeration of the question bank by guessing `_q{N}` suffixes. */
  public_slug?: string;
  question_text: string;
  question_number?: number;
  has_media?: boolean;
  media?: MediaItem[];
  exam?: { subject?: string; year?: number; year_ec?: number; exam_type?: string };
  metadata?: {
    topic?: string;
    subtopic?: string;
    correct_answer?: string;
    explanation?: string;
    distractor_rationale?: string;
  };
  options: { label: string; text: string; is_correct: boolean }[];
  /** Legacy top-level mirror — API actually puts it under metadata.explanation. */
  explanation?: string;
}

// Prefer the opaque public slug; fall back to question_id for rows that
// haven't been backfilled yet (shouldn't happen in prod after migration 059).
const shareIdFor = (q?: QuestionView | null): string =>
  (q?.public_slug || q?.question_id || '').toString();

const PublicQuestion: React.FC = () => {
  const { questionId } = useParams<{ questionId: string }>();
  const navigate = useNavigate();

  const [question, setQuestion] = useState<QuestionView | null>(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attemptCount, setAttemptCount] = useState(1);   // how many Q's they've tried
  const [gotRightCount, setGotRightCount] = useState(0); // streak counter
  const [loadingNext, setLoadingNext] = useState(false);
  const [savingCard, setSavingCard] = useState(false);
  const [explanationOpen, setExplanationOpen] = useState(true);
  const [lomiOpen, setLomiOpen] = useState(false);
  const [lomiTyping, setLomiTyping] = useState(false);
  const [lomiStage, setLomiStage] = useState<'initial' | 'revealed' | 'followup'>('initial');
  const [shareBundle, setShareBundle] = useState<ShareCardBundle | null>(null);
  const [shareStatus, setShareStatus] = useState<string>('');
  const mainRef = useRef<HTMLElement | null>(null);
  const shareCardRef = useRef<HTMLDivElement | null>(null);

  const closeSharePreview = useCallback(() => {
    setShareBundle(b => {
      if (b) URL.revokeObjectURL(b.objectUrl);
      return null;
    });
    setShareStatus('');
  }, []);

  const loadQuestion = useCallback(async (idOrHash: string) => {
    setLoading(true);
    setError(null);
    setRevealed(false);
    setPicked(null);
    setExplanationOpen(true); // show explanation by default on every question
    setLomiOpen(false);
    setLomiStage('initial');
    setLomiTyping(false);
    closeSharePreview();
    try {
      const resp = await fetch(`/api/questions/${encodeURIComponent(idOrHash)}`);
      if (!resp.ok) { setError(`We couldn't load this question.`); return; }
      const data = await resp.json();
      if (data?.success && data.question) {
        setQuestion(data.question);
      } else {
        setError(`This question isn't available.`);
      }
    } catch {
      setError('Network error — try again in a moment.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!questionId) return;
    loadQuestion(questionId);
  }, [questionId, loadQuestion]);

  const correctAnswer = (question?.metadata?.correct_answer || '').toUpperCase();
  const subject = question?.exam?.subject || 'ESSLCE';
  const year = question?.exam?.year_ec || question?.exam?.year;
  const topic = question?.metadata?.topic;

  const onReveal = () => {
    if (!picked) return;
    setRevealed(true);
    if (picked === correctAnswer) setGotRightCount(c => c + 1);
  };

  const onTryAnother = async () => {
    if (loadingNext) return;
    setLoadingNext(true);
    try {
      const params = new URLSearchParams();
      if (subject) params.set('subject', subject);
      // Exclude via slug so the user can't navigate back to the same
      // question; API accepts either id or slug here.
      const excludeKey = question?.public_slug || question?.question_id;
      if (excludeKey) params.set('exclude', excludeKey);
      const resp = await fetch(`/api/questions/random-public?${params.toString()}`);
      if (resp.ok) {
        const data = await resp.json();
        if (data?.question_id) {
          setAttemptCount(c => c + 1);
          // Navigate by slug if the API gave us one, so the opaque URL
          // is what ends up in the address bar.
          const nextKey = data.public_slug || data.question_id;
          await loadQuestion(nextKey);
          mainRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
      }
    } catch {}
    finally { setLoadingNext(false); }
  };

  const goSignup = (intent: 'ask' | 'weak-spots' | 'start') => {
    // Carry a hint via the URL; Login can honor it if we want to deep-link later.
    const params = new URLSearchParams({ from: 'share', intent });
    const qRef = shareIdFor(question);
    if (qRef) params.set('q', qRef);
    navigate(`/login?${params.toString()}`);
  };

  const renderRecipientMessage = () => {
    if (attemptCount === 1) {
      return (
        <div className="pq-ribbon">
          <span className="pq-ribbon-emoji">🍋</span>
          <span>A friend sent you a practice question</span>
        </div>
      );
    }
    if (gotRightCount >= 2) {
      return (
        <div className="pq-ribbon pq-ribbon-streak">
          <span className="pq-ribbon-emoji">🔥</span>
          <span>You're on a roll — {gotRightCount} in a row</span>
        </div>
      );
    }
    return (
      <div className="pq-ribbon">
        <span className="pq-ribbon-emoji">🎯</span>
        <span>One more from {subject}</span>
      </div>
    );
  };

  return (
    <div className="pq-page">
      <header className="pq-header">
        <div className="pq-brand" onClick={() => navigate('/')}>
          <Logo height={28} withAmharic />
        </div>
        <button className="pq-header-cta" onClick={() => goSignup('start')}>
          Sign in
        </button>
      </header>

      <main className="pq-main" ref={mainRef}>
        {loading && <div className="pq-loading">Loading…</div>}

        {error && (
          <div className="pq-error">
            <h2>{error}</h2>
            <p className="pq-meta">It may have been removed, or the link is incorrect.</p>
            <button className="pq-primary" onClick={() => goSignup('start')}>
              Open Lomi →
            </button>
          </div>
        )}

        {!loading && !error && question && (
          <>
            {/* Off-screen share card — real DOM so KaTeX + fonts render.
                Captured by html-to-image when the user taps Share. */}
            <div
              aria-hidden="true"
              style={{
                position: 'fixed',
                top: 0,
                left: '-99999px',
                pointerEvents: 'none',
                width: '1080px',
                height: '1920px',
              }}
            >
              <ShareCardRender
                ref={shareCardRef}
                questionText={question.question_text}
                options={question.options.map(o => ({ label: o.label, text: o.text }))}
                subject={subject}
                year={year}
                topic={topic}
                url={`${window.location.origin}/q/${shareIdFor(question)}`}
              />
            </div>

            {renderRecipientMessage()}

            <div className="pq-question-ribbon">
              📝 {subject}{year ? ` · ${year} ESSLCE` : ''}{topic ? ` · ${topic}` : ''}
            </div>

            <div className="pq-card">
              {question.media && question.media.length > 0 && (
                <MediaDisplay
                  media={question.media}
                  showCaption={true}
                  maxHeight={280}
                  allowZoom={true}
                />
              )}

              <div className="pq-question-text">
                <MarkdownRenderer>{question.question_text}</MarkdownRenderer>
              </div>

              <div className="pq-options">
                {question.options.map(opt => {
                  const label = opt.label.toUpperCase();
                  const isPicked = picked === label;
                  const isCorrect = revealed && label === correctAnswer;
                  const isWrong = revealed && isPicked && label !== correctAnswer;
                  const cls = [
                    'pq-option',
                    isPicked && !revealed ? 'picked' : '',
                    revealed && isPicked && !isCorrect && !isWrong ? 'your-pick' : '',
                    isCorrect ? 'correct' : '',
                    isWrong ? 'wrong' : '',
                  ].filter(Boolean).join(' ');
                  return (
                    <button
                      key={label}
                      className={cls}
                      disabled={revealed}
                      onClick={() => setPicked(label)}
                    >
                      <span className="pq-option-letter">{label}</span>
                      <span className="pq-option-text">
                        <MarkdownRenderer inline>{opt.text}</MarkdownRenderer>
                      </span>
                      {isPicked && !revealed && (
                        <span className="pq-option-check" aria-hidden>✓</span>
                      )}
                      {isCorrect && (
                        <span className="pq-option-badge pq-option-badge-correct" aria-label="correct">
                          ✓ Correct
                        </span>
                      )}
                      {isWrong && (
                        <span className="pq-option-badge pq-option-badge-wrong" aria-label="your answer">
                          Your pick
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {!revealed ? (
                <button className="pq-primary" disabled={!picked} onClick={onReveal}>
                  Check answer
                </button>
              ) : (
                <div className="pq-reveal">
                  <div className={`pq-verdict ${picked === correctAnswer ? 'right' : 'wrong'}`}>
                    {picked === correctAnswer
                      ? `✓ Nailed it — the answer is ${correctAnswer}.`
                      : `✗ The correct answer is ${correctAnswer}${picked ? ` (you picked ${picked})` : ''}.`}
                  </div>

                  {(() => {
                    const exp = question.metadata?.explanation || question.explanation;
                    return (
                      <div className="pq-explanation-block">
                        {exp ? (
                          <>
                            <button
                              type="button"
                              className="pq-explanation-toggle"
                              onClick={() => setExplanationOpen(v => !v)}
                              aria-expanded={explanationOpen}
                            >
                              <span>{explanationOpen ? '▼' : '▶'}</span>
                              <span>
                                {explanationOpen ? 'Hide explanation' : 'Show explanation'}
                              </span>
                            </button>
                            {explanationOpen && (
                              <div className="pq-explanation">
                                <MarkdownRenderer>{exp}</MarkdownRenderer>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="pq-explanation pq-explanation-fallback">
                            <p>
                              <strong>Answer: {correctAnswer}</strong>
                              {picked && picked !== correctAnswer
                                ? ` — not ${picked}.`
                                : '.'}
                            </p>
                            <p>
                              Want the reasoning laid out step-by-step? Ask Lomi AI below —
                              it'll walk you through the trap.
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Compact next-steps row: Try-another first (what most
                      students want after a reveal), then Ask Lomi for the
                      ones who want to dig in. Both slim so the reveal
                      doesn't balloon vertically. */}
                  <div className="pq-reveal-ctas">
                    <button
                      type="button"
                      className="pq-cta-compact pq-cta-try"
                      onClick={onTryAnother}
                      disabled={loadingNext}
                    >
                      <span className="pq-cta-icon">🎯</span>
                      <span className="pq-cta-label">
                        {loadingNext ? 'Loading…' : `Try another ${subject} question`}
                      </span>
                      <span className="pq-cta-arrow">→</span>
                    </button>

                    {!lomiOpen && (
                      <button
                        type="button"
                        className="pq-cta-compact pq-cta-lomi"
                        onClick={() => {
                          setLomiOpen(true);
                          setLomiTyping(true);
                          setLomiStage('initial');
                          setTimeout(() => {
                            setLomiTyping(false);
                            setLomiStage('revealed');
                          }, 900);
                        }}
                      >
                        <span className="pq-cta-icon">🍋</span>
                        <span className="pq-cta-label">Ask Lomi AI about this</span>
                        <span className="pq-cta-arrow">→</span>
                      </button>
                    )}
                  </div>

                  {lomiOpen && (
                    <div className="pq-chat">
                      <div className="pq-chat-header">
                        <span className="pq-chat-avatar">🍋</span>
                        <span className="pq-chat-name">Lomi AI</span>
                        <span className="pq-chat-status">Online</span>
                      </div>

                      <div className="pq-chat-body">
                        {/* Your question */}
                        <div className="pq-bubble user">
                          {picked === correctAnswer
                            ? `Why is ${correctAnswer} correct here?`
                            : `Why is ${correctAnswer} right and not ${picked || 'what I picked'}?`}
                        </div>

                        {lomiTyping && (
                          <div className="pq-bubble lomi typing">
                            <span className="pq-type-dot" />
                            <span className="pq-type-dot" />
                            <span className="pq-type-dot" />
                          </div>
                        )}

                        {lomiStage !== 'initial' && (
                          <div className="pq-bubble lomi">
                            {(question.metadata?.explanation || question.explanation) ? (
                              <MarkdownRenderer>
                                {(question.metadata?.explanation || question.explanation) as string}
                              </MarkdownRenderer>
                            ) : (
                              <>
                                <strong>{correctAnswer}</strong> is the answer.
                                Want me to walk through why the other options are
                                traps, and how this shows up on other ESSLCE papers?
                              </>
                            )}
                          </div>
                        )}

                        {lomiStage === 'followup' && (
                          <div className="pq-bubble lomi">
                            I can break this down further, generate practice
                            questions on the exact same trap, or show you past
                            years where this came up.
                            <strong>Sign in and I'll keep going.</strong>
                          </div>
                        )}

                        {lomiStage === 'revealed' && (
                          <div className="pq-chat-follow">
                            <button
                              type="button"
                              className="pq-chat-followbtn"
                              onClick={() => {
                                setLomiTyping(true);
                                setTimeout(() => {
                                  setLomiTyping(false);
                                  setLomiStage('followup');
                                }, 700);
                              }}
                            >
                              🔎 Go deeper on this trap
                            </button>
                            <button
                              type="button"
                              className="pq-chat-followbtn"
                              onClick={() => goSignup('ask')}
                            >
                              🎯 Give me a similar question
                            </button>
                          </div>
                        )}
                      </div>

                      {lomiStage === 'followup' && (
                        <button
                          type="button"
                          className="pq-chat-cta"
                          onClick={() => goSignup('ask')}
                        >
                          Open Lomi — free to sign in
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {revealed && (
              <>
                {/* Try-another + Ask-Lomi live inside the card now (see
                    pq-reveal-ctas above). Out here we keep only the
                    signup nudge + share row. */}
                <button
                  className="pq-secondary-link"
                  onClick={() => goSignup('weak-spots')}
                >
                  📚 Save my progress & find my weak spots →
                </button>

                <div className="pq-share-row">
                  <ShareButton
                    url={`/q/${shareIdFor(question)}`}
                    title={`ESSLCE practice · ${subject}`}
                    text={`Try this ${subject} question on Lomi 🍋`}
                    className="pq-share-btn"
                    label="Share link"
                  />
                  <CopyLinkButton
                    url={`/q/${shareIdFor(question)}`}
                    className="pq-share-btn"
                    label="Copy link"
                  />
                  <button
                    type="button"
                    className="pq-share-btn pq-share-btn-tiktok"
                    disabled={savingCard}
                    aria-label="Share card to TikTok or Instagram"
                    onClick={async () => {
                      if (savingCard) return;
                      if (!shareCardRef.current) return;
                      setSavingCard(true);
                      setShareStatus('');
                      try {
                        const bundle = await captureShareCardNode(
                          shareCardRef.current,
                          `lomi-${subject?.toLowerCase() || 'question'}.png`,
                        );
                        setShareBundle(bundle);
                      } catch (err) {
                        console.warn('Share card failed', err);
                      } finally {
                        setSavingCard(false);
                      }
                    }}
                  >
                    <svg
                      className="pq-tiktok-icon"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <path
                        fill="currentColor"
                        d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.298-.002.595.042.88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z"
                      />
                    </svg>
                    <span>{savingCard ? 'Rendering…' : 'Share to TikTok / IG'}</span>
                  </button>
                </div>

                {attemptCount >= 2 && (
                  <div className="pq-conversion">
                    <div className="pq-conversion-title">
                      You're doing what matters — practicing with real exam questions.
                    </div>
                    <p className="pq-conversion-body">
                      Lomi remembers what you missed, explains the trap, and keeps you
                      practicing the topics you'll see on exam day. It's free.
                    </p>
                    <button className="pq-primary pq-primary-xl" onClick={() => goSignup('start')}>
                      Save my progress — open Lomi
                    </button>
                    <p className="pq-meta">One tap — Telegram or Gmail</p>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>

      {/* Sticky signup bar — always visible, non-intrusive */}
      {!loading && !error && (
        <div className="pq-sticky-bar">
          <span className="pq-sticky-text">🍋 Get your full exam prep on Lomi</span>
          <button className="pq-sticky-cta" onClick={() => goSignup('start')}>
            Start free →
          </button>
        </div>
      )}

      {/* Share preview modal — shows the generated card + action buttons so
          the student sees what they're sharing + has an unambiguous path. */}
      {shareBundle && question && (
        <div
          className="pq-share-modal"
          role="dialog"
          aria-modal="true"
          onClick={closeSharePreview}
        >
          <div className="pq-share-modal-body" onClick={e => e.stopPropagation()}>
            <div className="pq-share-modal-head">
              <span>Your share card</span>
              <button
                type="button"
                className="pq-share-modal-close"
                onClick={closeSharePreview}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <img
              src={shareBundle.objectUrl}
              alt="Lomi share card"
              className="pq-share-modal-image"
            />

            <p className="pq-share-modal-hint">
              📱 Long-press the image to save it, or use a button below:
            </p>

            <div className="pq-share-modal-actions">
              {shareBundle.canNativeShare && (
                <button
                  type="button"
                  className="pq-share-modal-btn primary"
                  onClick={async () => {
                    if (!shareBundle) return;
                    const input: ShareCardInput = {
                      questionText: question.question_text,
                      subject: subject,
                      year: year,
                      topic: topic,
                      url: `${window.location.origin}/q/${shareIdFor(question)}`,
                    };
                    setShareStatus('Opening share sheet…');
                    const outcome = await nativeShareBundle(shareBundle, input);
                    if (outcome === 'shared') setShareStatus('✓ Shared');
                    else if (outcome === 'cancelled') setShareStatus('');
                    else setShareStatus("Your browser didn't open the share sheet. Use Download instead.");
                  }}
                >
                  📲 Open share sheet
                </button>
              )}

              <button
                type="button"
                className="pq-share-modal-btn"
                onClick={() => {
                  downloadBundle(shareBundle);
                  setShareStatus('✓ Image downloaded. Open TikTok/IG and pick it from your gallery.');
                }}
              >
                ⬇ Download image
              </button>
            </div>

            {shareStatus && (
              <p className="pq-share-modal-status">{shareStatus}</p>
            )}

            <div className="pq-share-modal-how">
              <strong>How to post on TikTok:</strong>
              <ol>
                <li>Save or share the image above.</li>
                <li>Open TikTok → tap <b>+</b> → <b>Story</b> or <b>Post</b>.</li>
                <li>Pick the Lomi card from your photos.</li>
                <li>Add a caption. Friends scan the QR to try Lomi.</li>
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicQuestion;
